<template lang="pug">
  v-container(fluid)
    v-card
      v-card-title.d-flex.justify-space-between
        span Gestión de Blog
        v-btn(
          color="primary"
          @click="openArticleDialog()"
        )
          v-icon(left) mdi-plus
          | Nuevo Artículo
  
      v-card-text
        // Filters
        v-row.mb-4
          v-col(cols="12" sm="4")
            v-text-field(
              v-model="search"
              label="Buscar artículos"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
            )
          v-col(cols="12" sm="4")
            v-select(
              v-model="categoryFilter"
              :items="categories"
              label="Filtrar por categoría"
              outlined
              dense
              clearable
            )
          v-col(cols="12" sm="4")
            v-select(
              v-model="statusFilter"
              :items="statusOptions"
              label="Estado"
              outlined
              dense
              clearable
            )
  
        // Articles Table
        v-data-table(
          :headers="headers"
          :items="filteredArticles"
          :loading="loading"
          :search="search"
        )
          template(v-slot:item.image="{ item }")
            v-avatar(size="40")
              v-img(:src="item.image")
  
          template(v-slot:item.category="{ item }")
            v-chip(
              small
              :color="getCategoryColor(item.category)"
              label
            ) {{ item.category }}
  
          template(v-slot:item.publishedAt="{ item }")
            | {{ formatDate(item.publishedAt) }}
  
          template(v-slot:item.status="{ item }")
            v-chip(
              small
              :color="getStatusColor(item.status)"
            ) {{ getStatusText(item.status) }}
  
          template(v-slot:item.actions="{ item }")
            v-btn(
              icon
              small
              class="mr-2"
              @click="openArticleDialog(item)"
            )
              v-icon mdi-pencil
            v-btn(
              icon
              small
              :href="'/blog/' + item.slug"
              target="_blank"
            )
              v-icon mdi-eye
            v-btn(
              icon
              small
              color="error"
              @click="deleteItem(item)"
            )
              v-icon mdi-delete
  
      // Article Dialog
      v-dialog(
        v-model="dialog"
        max-width="1200"
        persistent
      )
        v-card
          v-card-title
            | {{ editedItem._id ? 'Editar' : 'Nuevo' }} Artículo
            v-spacer
            v-btn(
              icon
              @click="closeDialog"
            )
              v-icon mdi-close
  
          v-card-text
            v-form(ref="form" v-model="valid")
              v-row
                v-col(cols="12" md="8")
                  v-text-field(
                    v-model="editedItem.title"
                    label="Título"
                    :rules="titleRules"
                    required
                    outlined
                  )

                  v-select(
                    v-model="editedItem.restaurant"
                    :items="restaurants"
                    item-text="name"
                    item-value="_id"
                    label="Restaurante"
                    outlined
                    clearable
                    :loading="loadingRestaurants"
                  )
  
                  v-file-input(
                    v-model="imageFile"
                    :rules="imageRules"
                    accept="image/*"
                    label="Imagen principal"
                    prepend-icon="mdi-camera"
                    :required="!editedItem._id"
                    show-size
                    @change="handleImageUpload"
                  )
  
                  v-img(
                    v-if="editedItem.image"
                    :src="editedItem.image"
                    max-height="300"
                    contain
                    class="grey lighten-2 mb-4"
                  )
  
                  ckeditor(
                    :editor="editor"
                    v-model="editedItem.content"
                    :config="editorConfig"
                    @ready="onEditorReady"
                  )
  
                v-col(cols="12" md="4")
                  v-select(
                    v-model="editedItem.category"
                    :items="categories"
                    label="Categoría"
                    :rules="[v => !!v || 'La categoría es requerida']"
                    required
                    outlined
                  )
  
                  v-text-field.mb-2(
                    :value="editedItem.tagsString || editedItem.tags.join(', ')"
                    label="Tags"
                    hint="Ingresa los tags separados por coma"
                    persistent-hint
                    outlined
                    @input="handleTagsInput"
                  )
  
                  v-text-field(
                    v-model="editedItem.description"
                    label="Descripción corta"
                    :rules="descriptionRules"
                    required
                    outlined
                  )
  
                  v-menu(
                    ref="dateMenu"
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  )
                    template(v-slot:activator="{ on, attrs }")
                      v-text-field(
                        v-model="editedItem.publishedAt"
                        label="Fecha de publicación"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                      )
                    v-date-picker(
                      v-model="editedItem.publishedAt"
                      @input="dateMenu = false"
                    )
  
                  v-select(
                    v-model="editedItem.status"
                    :items="statusOptions"
                    label="Estado"
                    outlined
                  )
  
          v-card-actions
            v-spacer
            v-btn(
              text
              @click="closeDialog"
            ) Cancelar
            v-btn(
              color="primary"
              :disabled="!valid"
              @click="save"
            ) Guardar
  
      // Delete Confirmation Dialog
      v-dialog(
        v-model="deleteDialog"
        max-width="400"
      )
        v-card
          v-card-title Eliminar Artículo
          v-card-text
            | ¿Estás seguro de eliminar el artículo "{{ articleToDelete?.title }}"?
            | Esta acción no se puede deshacer.
          v-card-actions
            v-spacer
            v-btn(
              text
              @click="deleteDialog = false"
            ) Cancelar
            v-btn(
              color="error"
              @click="confirmDelete"
            ) Eliminar
  </template>
  
  <script>
  import { format } from 'date-fns';
  import { es } from 'date-fns/locale';
  import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
  import { mapState, mapActions } from 'vuex';
  
  export default {
    name: 'BlogManager',
  
    data: () => ({
      editor: ClassicEditor,
      editorConfig: {
        toolbar: {
          shouldNotGroupWhenFull: true,
          items: [
            'heading',
            '|',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            '|',
            'fontSize',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'link',
            'imageUpload',
            'mediaEmbed',
            '|',
            'bulletedList',
            'numberedList',
            'outdent',
            'indent',
            '|',
            'alignment',
            'blockQuote',
            'insertTable',
            'tableColumn',
            'tableRow',
            'mergeTableCells',
            '|',
            'undo',
            'redo'
          ],
          removeItems: [],
          viewportTopOffset: 60
        },
        heading: {
          options: [
            { model: 'paragraph', title: 'Párrafo', class: 'ck-heading_paragraph' },
            { model: 'heading2', view: 'h2', title: 'Encabezado 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Encabezado 3', class: 'ck-heading_heading3' }
          ]
        },
        image: {
          toolbar: [
            'imageStyle:inline',
            'imageStyle:block',
            'imageStyle:side',
            '|',
            'toggleImageCaption',
            'imageTextAlternative'
          ]
        },
        table: {
          contentToolbar: [
            'tableColumn',
            'tableRow',
            'mergeTableCells'
          ]
        },
        language: 'es'
      },
      loading: false,
      dialog: false,
      deleteDialog: false,
      valid: false,
      search: '',
      categoryFilter: null,
      statusFilter: null,
      dateMenu: false,
      imageFile: null,
      editedItem: {
        title: '',
        description: '',
        content: '',
        image: '',
        category: '',
        restaurant: null,
        tags: [],
        tagsString: '',
        publishedAt: new Date().toISOString().substr(0, 10),
        status: 'draft'
      },
      defaultItem: {
        title: '',
        description: '',
        content: '',
        image: '',
        category: '',
        restaurant: null,
        tags: [],
        tagsString: '',
        publishedAt: new Date().toISOString().substr(0, 10),
        status: 'draft'
      },
      articleToDelete: null,
      titleRules: [
        v => !!v || 'El título es requerido',
        v => v.length <= 100 || 'El título debe tener menos de 100 caracteres'
      ],
      descriptionRules: [
        v => !!v || 'La descripción es requerida',
        v => v.length <= 200 || 'La descripción debe tener menos de 200 caracteres'
      ],
      contentRules: [
        v => !!v || 'El contenido es requerido'
      ],
      imageRules: [
        v => !v || v.size < 5000000 || 'La imagen debe ser menor a 5MB'
      ],
      categories: [
        'Nuevas Aperturas',
        'Eventos Especiales',
        'Menús Destacados',
        'Entrevistas',
        'Recetas',
        'Tips & Consejos'
      ],
      statusOptions: [
        { text: 'Borrador', value: 'draft' },
        { text: 'Publicado', value: 'published' },
        { text: 'Archivado', value: 'archived' }
      ],
    }),
  
    computed: {
      ...mapState('blog', [
        'articles',
        'loading',
        'restaurants',
        'loadingRestaurants'
      ]),
  
      headers() {
        return [
          { text: 'Título', value: 'title' },
          { text: 'Categoría', value: 'category' },
          { text: 'Restaurante', value: 'restaurant.name' },
          { text: 'Estado', value: 'status' },
          { text: 'Fecha', value: 'publishedAt' },
          { text: 'Acciones', value: 'actions', sortable: false }
        ]
      },
  
      filteredArticles() {
        return this.articles.filter(article => {
          if (this.categoryFilter && article.category !== this.categoryFilter) {
            return false;
          }
          if (this.statusFilter && article.status !== this.statusFilter) {
            return false;
          }
          return true;
        });
      }
    },
  
    methods: {
      ...mapActions('blog', [
        'fetchArticles',
        'fetchRestaurants',
        'createArticle',
        'updateArticle',
        'deleteArticle'
      ]),
  
      onEditorReady(editor) {
        // Editor is ready to use
        console.log('Editor is ready', editor);
      },
  
      formatDate(date) {
        return format(new Date(date), "d 'de' MMMM, yyyy", { locale: es });
      },
  
      getCategoryColor(category) {
        const colors = {
          'Nuevas Aperturas': 'success',
          'Eventos Especiales': 'primary',
          'Menús Destacados': 'warning',
          'Entrevistas': 'info',
          'Recetas': 'purple',
          'Tips & Consejos': 'orange'
        };
        return colors[category] || 'grey';
      },
  
      getStatusColor(status) {
        return {
          draft: 'grey',
          published: 'success',
          archived: 'error'
        }[status];
      },
  
      getStatusText(status) {
        return {
          draft: 'Borrador',
          published: 'Publicado',
          archived: 'Archivado'
        }[status];
      },
  
      async handleImageUpload(file) {
        if (!file) return;
  
        try {
          const formData = new FormData();
          formData.append('image', file);
          
          const { data } = await this.$http.post('/blog/upload-image', formData);
          this.editedItem.image = data.url;
        } catch (error) {
          this.$toast.error('Error al subir la imagen');
        }
      },
  
      handleTagsInput(value) {
        this.editedItem.tagsString = value
        this.editedItem.tags = value
          .split(',')
          .map(tag => tag.trim())
          .filter(tag => tag !== '')
      },
  
      openArticleDialog(item = null) {
        this.editedItem = item ? { ...item } : {
          title: '',
          description: '',
          content: '',
          image: '',
          category: '',
          restaurant: null,
          tags: [],
          tagsString: '',
          publishedAt: new Date().toISOString().substr(0, 10),
          status: 'draft'
        };
        this.imageFile = null;
        this.dialog = true;
      },
  
      closeDialog() {
        this.dialog = false;
        this.$nextTick(() => {
          this.$refs.form?.reset();
        });
      },
  
      async save() {
        if (!this.$refs.form.validate()) return
        
        this.loading = true
        try {
          const formData = new FormData()
          formData.append('title', this.editedItem.title)
          formData.append('content', this.editedItem.content)
          formData.append('description', this.editedItem.description)
          formData.append('category', this.editedItem.category)
          formData.append('restaurant', this.editedItem.restaurant)
          
          const tags = Array.isArray(this.editedItem.tags) 
            ? this.editedItem.tags 
            : this.editedItem.tags.split(',').map(tag => tag.trim())
          formData.append('tags', JSON.stringify(tags))

          if (this.imageFile) {
            formData.append('image', this.imageFile)
          }

          if (this.editedItem._id) {
            formData.append('_id', this.editedItem._id)
            await this.updateArticle({ id: this.editedItem._id, formData })
          } else {
            await this.createArticle(formData)
          }

          this.closeDialog()
          this.$toast.success(this.editedItem._id ? 'Artículo actualizado' : 'Artículo creado')
        } catch (error) {
          this.$toast.error('Error al guardar el artículo')
        } finally {
          this.loading = false
        }
      },
  
      async deleteItem(item) {
        if (!confirm('¿Estás seguro de eliminar este artículo?')) return

        try {
          await this.deleteArticle(item._id)
          this.$toast.success('Artículo eliminado')
        } catch (error) {
          this.$toast.error('Error al eliminar el artículo')
        }
      },
  
      async confirmDelete() {
        try {
          await this.deleteArticle(this.articleToDelete._id);
          this.$toast.success('Artículo eliminado');
          this.deleteDialog = false;
          this.articleToDelete = null;
        } catch (error) {
          this.$toast.error('Error al eliminar el artículo');
        }
      },
    },
  
    async created() {
      try {
        this.loading = true
        await Promise.all([
          this.fetchArticles(),
          this.fetchRestaurants()
        ])
      } catch (error) {
        this.$toast.error('Error al cargar los datos')
      } finally {
        this.loading = false
      }
    }
  };
  </script>